import type { Building } from "building-types/building/buildingTypes";
import type { Foundation, FoundationSystem } from "building-types/foundations/foundationSystemTypes";
import type { RWTable, Table } from "common/data/table";
import { buildEdgeModifiersByParentId, getEntityEdgeModifiersTable, polyLine2Modify } from "doc/utils/modifierUtils";
import { createFoundationInstances } from "../generationTools/components/componentHorizontalAssemblies";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: { optionSets, instanceOptionSets, foundationSystems, foundations, edgeModifiers },
  idToParents,
  componentToTransforms,
  levels,
  mirror,
  mirrorTranslationX,
  optionChoices,
  enabledFeatures,
  isolatedEntity,
  modifiers,
}: Building) {
  return {
    docFoundationSystems: foundationSystems,
    docFoundations: foundations,
    definitions: { optionSets, instanceOptionSets },
    rawOptionChoices: optionChoices,
    idToParents,
    componentToTransforms,
    levels,
    mirror,
    mirrorTranslationX,
    modelComponents: enabledFeatures.modelComponents,
    isolatedEntity,
    edgeModifiers,
    modifiers,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  docFoundationSystems: "shallow",
  docFoundations: "shallow",
  definitions: "shallow",
  rawOptionChoices: "shallow",
  idToParents: "shallow",
  componentToTransforms: "shallow",
  levels: "shallow",
  modelComponents: "shallow",
  isolatedEntity: "shallow",
  edgeModifiers: "deep",
  modifiers: "shallow",
});

/**
 * Applies stored edge modifiers to each foundation's perimeter in a building-copy-only,
 * non-destructive way (same contract as room/floor edge modifiers). `doc.foundations` is
 * never mutated.
 */
function applyEdgeModifiersToFoundations(
  docFoundations: Table<Foundation>,
  edgeModifiers: Building["doc"]["edgeModifiers"],
  modifiers: Building["modifiers"],
): Table<Foundation> {
  const edgeModifiersByParentId = buildEdgeModifiersByParentId(edgeModifiers);
  const modifiedFoundations: RWTable<Foundation> = {};

  for (const foundationId in docFoundations) {
    const foundation = docFoundations[foundationId];
    if (!foundation.perimeter || !foundation.edgeIds) {
      modifiedFoundations[foundationId] = foundation;
      continue;
    }
    const foundationEdgeModifiers = getEntityEdgeModifiersTable(edgeModifiersByParentId, foundationId);
    modifiedFoundations[foundationId] = {
      ...foundation,
      perimeter: polyLine2Modify(foundation.perimeter, foundationEdgeModifiers, modifiers, foundation.edgeIds),
    };
  }

  return modifiedFoundations;
}

export function generateComponentFoundations(input: ReturnType<typeof select>): {
  horizontalAssemblyFoundations: Table<Foundation>;
  horizontalAssemblyFoundationSystems: Table<FoundationSystem>;
} {
  const { docFoundationSystems, idToParents, isolatedEntity, edgeModifiers, modifiers } = input;

  const docFoundations = applyEdgeModifiersToFoundations(input.docFoundations, edgeModifiers, modifiers);

  const archetypeFoundations: RWTable<Foundation> = {};
  const archetypeFoundationSystems: RWTable<FoundationSystem> = {};

  for (const foundationSystemId in docFoundationSystems) {
    if (shouldGenerateEntity(foundationSystemId, idToParents, isolatedEntity)) {
      archetypeFoundationSystems[foundationSystemId] = docFoundationSystems[foundationSystemId];
    }
  }
  for (const foundationId in docFoundations) {
    const foundation = docFoundations[foundationId];
    if (archetypeFoundationSystems[foundation.parentId]) {
      archetypeFoundations[foundationId] = foundation;
    }
  }

  if (!input.modelComponents) {
    return {
      horizontalAssemblyFoundations: archetypeFoundations,
      horizontalAssemblyFoundationSystems: archetypeFoundationSystems,
    };
  }

  const { instanceFoundationSystems, instanceFoundations } = createFoundationInstances(
    docFoundationSystems,
    docFoundations,
    input,
  );

  return {
    horizontalAssemblyFoundations: { ...archetypeFoundations, ...instanceFoundations },
    horizontalAssemblyFoundationSystems: { ...archetypeFoundationSystems, ...instanceFoundationSystems },
  };
}

export default memo(select, generateComponentFoundations, compare);
